import { ControlsGroupInputProps, IValidationTarget } from "aic";

/**
 * Defines the input fields for creating a new report, including validation and computed values.
 *
 * @param existingNames - List of existing report names to check for duplicates
 * @param reportTypes - Available report types for the dropdown
 * @param reportSuffixes - Available report suffixes for the dropdown
 * @returns Array of ControlsGroupInputProps defining the input fields
 */
export const newReportInputs = (
  existingNames: string[],
  reportTypes: string[],
  reportSuffixes: string[],
): ControlsGroupInputProps[] => [
  {
    inputLabel: "Report type",
    inputName: "ReportType",
    dropdownOptions: reportTypes,
    validCondition: async (_backEndUrl, target) => {
      const reportType = target.value;
      const isValid = reportType !== "";
      return {
        success: true,
        data: {
          valid: isValid,
          validationText: isValid ? "" : "Report type is required",
        },
      };
    },
  },
  {
    inputLabel: "Report number",
    inputName: "Report number",
    validCondition: async (_: string, target: IValidationTarget) => {
      const reportNumber = target.value;
      const isValid = reportNumber === "" || /^\d{2}$/.test(reportNumber);
      return {
        success: true,
        data: {
          valid: isValid,
          validationText: isValid
            ? ""
            : "Report number must be exactly two digits (e.g., 01) or blank",
        },
      };
    },
    placeholder: "00",
    hidden: (values) => values["ReportType"] === "",
  },
  {
    inputLabel: "Report suffix",
    inputName: "Report suffix",
    dropdownOptions: ["", ...reportSuffixes],
    hidden: (values) => values["ReportType"] === "",
  },
  {
    inputLabel: "Custom suffix",
    inputName: "Custom suffix",
    hidden: (values) =>
      values["Report suffix"] !== "<<other>>" && values["Report suffix"] !== "<<country>>",
    validCondition: async (_backEndUrl, target) => {
      const customSuffix = target.value.trim();
      const validationText =
        customSuffix.length === 0
          ? "Custom suffix is required"
          : !/^[A-Za-z0-9]+$/.test(customSuffix)
            ? "Custom suffix can only contain letters and numbers"
            : "";
      return {
        success: true,
        data: {
          valid: validationText === "",
          validationText,
        },
      };
    },
  },
  {
    inputLabel: "Suffix number",
    inputName: "Suffix number",
    validCondition: async (_: string, target: IValidationTarget) => {
      const suffixNumber = target.value;
      const isValid = suffixNumber === "" || /^\d{2}$/.test(suffixNumber);
      return {
        success: true,
        data: {
          valid: isValid,
          validationText: isValid
            ? ""
            : "Suffix number must be exactly two digits (e.g., 01) or blank",
        },
      };
    },
    hidden: (values) => values["Report suffix"] === "",
    placeholder: "00",
  },
  {
    inputLabel: "Name",
    inputName: "Name",
    computeValue: (values) => {
      const suffix =
        values["Report suffix"] ??
        // istanbul ignore next
        "";
      const customSuffix =
        values["Custom suffix"] ??
        // istanbul ignore next
        "";
      const suffixNumber =
        values["Suffix number"] ??
        // istanbul ignore next
        "";

      if (suffix === "")
        return `${
          values["ReportType"] ??
          // istanbul ignore next
          ""
        }${
          values["Report number"] ??
          // istanbul ignore next
          ""
        }`;

      const actualSuffix =
        suffix === "<<other>>" || suffix === "<<country>>" ? customSuffix : suffix;
      return `${
        values["ReportType"] ??
        // istanbul ignore next
        ""
      }${
        values["Report number"] ??
        // istanbul ignore next
        ""
      }_${actualSuffix}${suffixNumber}`;
    },
    readonly: true,
    validCondition: async (_: string, target: IValidationTarget) => {
      const reportName = target.value.trim();
      const isDuplicate = existingNames.includes(reportName.toLowerCase());
      return {
        success: true,
        data: {
          valid: !isDuplicate,
          validationText: isDuplicate ? "This name is taken" : "",
        },
      };
    },
  },
  {
    inputLabel: "Description",
    inputName: "Description",
    validCondition: async (_: string, target: IValidationTarget) => {
      const description = target.value.trim();
      const isValid = description.length > 0;
      return {
        success: true,
        data: {
          valid: isValid,
          validationText: isValid ? "" : "Description is required",
        },
      };
    },
  },
];
